import { useQueryClient, useMutation, useQuery } from '@tanstack/react-query';
import { Alert, Page, PageHeader, Table, type Action, type Column } from '@evogard/ui';
import type { BreakOverrun } from '@evogard/contract';
import { api, ok } from '../../lib/api.js';
import { can, useSessionContext } from '../../lib/session.js';
import { BREAK_OVERRUNS_KEY } from '../../components/BreakOverrunsBadge.js';
import { clockOf } from './breaks.js';

/**
 * Os estouros em aberto — quem não encerrou o intervalo até 1 minuto depois do
 * fim previsto, e ninguém dispensou. Some da lista quando a pessoa encerra.
 * É a mesma consulta do botão vermelho do topo.
 */
export function OverrunsScreen() {
  const { data: context } = useSessionContext();
  const tenantId = context?.currentTenant.id;
  // o líder dispensa os da equipe — a lista já vem recortada pela API
  const canDismiss =
    can(context, 'breaks.overruns', 'dispensar') || can(context, 'breaks.records', 'liderar');
  const client = useQueryClient();

  const overruns = useQuery({
    queryKey: [...BREAK_OVERRUNS_KEY, tenantId],
    enabled: tenantId !== undefined,
    queryFn: async () =>
      ok(await api.breaks.overruns(), 200, 'Não foi possível carregar os estouros.'),
    refetchInterval: 60_000,
  });

  const dismiss = useMutation({
    mutationFn: async (id: number) =>
      ok(
        await api.breaks.dismissOverrun({ params: { id }, body: {} }),
        204,
        'Não foi possível dispensar.',
      ),
    onSettled: () => client.invalidateQueries({ queryKey: BREAK_OVERRUNS_KEY }),
  });

  const columns: Column<BreakOverrun>[] = [
    { title: 'Pessoa', cell: (o) => o.userName, alignment: 'inicio' },
    { title: 'Ativou às', cell: (o) => clockOf(o.startedAt) },
    { title: 'Previsto até', cell: (o) => clockOf(o.expectedEndAt) },
    {
      title: 'Passou',
      cell: (o) => `${Math.max(0, Math.round((Date.now() - Date.parse(o.expectedEndAt)) / 60_000))} min`,
      alignment: 'fim',
    },
  ];

  const actions = (o: BreakOverrun): Action[] => [
    {
      label: 'Dispensar o aviso',
      icon: 'fechar',
      disabled: canDismiss ? false : 'Dispensar pede a permissão de dispensar estouros.',
      run: () => dismiss.mutate(o.id),
    },
  ];

  return (
    <Page measure="cheia">
      <PageHeader title="Intervalos estourados">
        Quem ativou o intervalo e não encerrou até 1 minuto depois do fim previsto. Dispensar
        tira o aviso; o registro continua na relação.
      </PageHeader>
      {overruns.isError && <Alert tone="erro">{overruns.error.message}</Alert>}
      {dismiss.isError && <Alert tone="erro">{dismiss.error.message}</Alert>}
      <Table
        label="Intervalos estourados"
        columns={columns}
        items={overruns.data ?? []}
        rowKey={(o) => String(o.id)}
        rowTitle={(o) => `Intervalo de ${o.userName}`}
        rowActions={actions}
        loading={overruns.isLoading}
        empty="Nenhum intervalo estourado agora."
      />
    </Page>
  );
}
